import React, { useRef, useState } from 'react';
import { usePersonContact } from '../contexts/PersonContactsContext';
import './PersonContactHover.css';

function PersonContactPopup({ contact, loading }) {
  if (loading) {
    return <p className="person-contact-popup-muted">Loading contact…</p>;
  }

  const fac = contact?.facility;
  const mobile = contact?.mobile?.trim();
  const email = contact?.email?.trim();
  const hasDirect = contact?.hasDirectContact;
  const hasFacility = !!(fac?.addressLine || fac?.name);
  const hasContact = !!(mobile || email || hasFacility);

  if (!hasContact) {
    return (
      <p className="person-contact-popup-muted">
        Contact not on file. Email and phone require VitalEngine user-directory API access.
      </p>
    );
  }

  return (
    <>
    <dl className="person-contact-popup-dl">
      <div className="person-contact-popup-row">
        <dt>Phone</dt>
        <dd>
          {mobile ? <a href={`tel:${mobile}`}>{mobile}</a> : '—'}
        </dd>
      </div>
      <div className="person-contact-popup-row">
        <dt>Email</dt>
        <dd>
          {email ? <a href={`mailto:${email}`}>{email}</a> : '—'}
        </dd>
      </div>
      {(fac?.name || fac?.addressLine) && (
        <>
          {fac?.name && (
            <div className="person-contact-popup-row">
              <dt>Facility</dt>
              <dd>{fac.name}</dd>
            </div>
          )}
          {fac?.addressLine && (
            <div className="person-contact-popup-row">
              <dt>Address</dt>
              <dd>
                {fac.mapsUrl ? (
                  <a href={fac.mapsUrl} target="_blank" rel="noopener noreferrer">
                    {fac.addressLine}
                  </a>
                ) : (
                  fac.addressLine
                )}
              </dd>
            </div>
          )}
        </>
      )}
    </dl>
    {!hasDirect && hasFacility && (
      <p className="person-contact-popup-note">
        Email and phone not on file for this user in the VitalEngine directory.
      </p>
    )}
    </>
  );
}

export default function PersonContactHover({ userId, children, className = '' }) {
  const id = Number(userId);
  const { contact, resolved } = usePersonContact(id);
  const [open, setOpen] = useState(false);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });
  const hideTimer = useRef(null);
  const anchorRef = useRef(null);

  if (!Number.isFinite(id) || id <= 0) {
    return <span className={className}>{children}</span>;
  }

  const positionPopup = () => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPopupPos({ top: rect.bottom + 6, left: rect.left });
  };

  const show = () => {
    clearTimeout(hideTimer.current);
    positionPopup();
    setOpen(true);
  };

  const hide = () => {
    hideTimer.current = setTimeout(() => setOpen(false), 120);
  };

  const loading = open && !resolved;
  const mobile = contact?.mobile?.trim();
  const email = contact?.email?.trim();
  const hasContactData = !!(mobile || email);
  const showContactHighlight = resolved && hasContactData;

  return (
    <span
      ref={anchorRef}
      className={`person-contact-hover${showContactHighlight ? ' person-contact-hover--has-contact' : ''} ${className}`.trim()}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      <span className="person-contact-hover-name">{children}</span>
      {open && (
        <div
          className="person-contact-popup person-contact-popup-fixed"
          role="tooltip"
          style={{ top: popupPos.top, left: popupPos.left }}
          onMouseEnter={show}
          onMouseLeave={hide}
        >
          <PersonContactPopup contact={contact} loading={loading} />
        </div>
      )}
    </span>
  );
}
